// client/src/components/secure/ActivityTimeline.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const ACTIVITY_TYPES = ['Note', 'Call', 'Email', 'Text', 'Meeting'];

// Log notes/calls/emails/texts/meetings against a client and/or a transaction.
// Pass clientId, transactionId, or both — whichever is given scopes the query and the logged entry.
const ActivityTimeline = ({ clientId, transactionId }) => {
    const [activities, setActivities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formData, setFormData] = useState({ activity_type: 'Note', direction: 'N/A', subject: '', body: '', occurred_at: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    const fetchActivities = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams();
        if (clientId) params.set('client_id', clientId);
        if (transactionId) params.set('transaction_id', transactionId);
        apiClient.get(`/api/activities?${params.toString()}`)
            .then(data => setActivities(data))
            .catch(err => setError(err.message || 'Failed to load activity timeline.'))
            .finally(() => setIsLoading(false));
    }, [clientId, transactionId, apiClient]);

    useEffect(() => {
        fetchActivities();
    }, [fetchActivities]);

    const nowLocalDatetime = () => {
        const d = new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().slice(0, 16);
    };

    const openForm = () => {
        setFormData({ activity_type: 'Note', direction: 'N/A', subject: '', body: '', occurred_at: nowLocalDatetime() });
        setIsFormOpen(true);
    };

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            await apiClient.post('/api/activities', {
                ...formData,
                client_id: clientId || null,
                transaction_id: transactionId || null,
            });
            setIsFormOpen(false);
            fetchActivities();
        } catch (err) {
            setError(err.message || 'Failed to log activity.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white p-8 rounded-lg shadow mt-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-800">Activity Timeline</h2>
                <button onClick={openForm} className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900">
                    Log Activity
                </button>
            </div>

            {isFormOpen && (
                <form onSubmit={handleSubmit} className="bg-slate-50 p-4 rounded-lg mb-6 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <select name="activity_type" value={formData.activity_type} onChange={handleChange} className="p-2 border rounded">
                            {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <select name="direction" value={formData.direction} onChange={handleChange} className="p-2 border rounded">
                            <option value="N/A">N/A</option>
                            <option value="Inbound">Inbound</option>
                            <option value="Outbound">Outbound</option>
                        </select>
                        <input type="datetime-local" name="occurred_at" value={formData.occurred_at} onChange={handleChange} required className="p-2 border rounded" />
                    </div>
                    <input name="subject" value={formData.subject} onChange={handleChange} placeholder="Subject (optional)" className="p-2 border rounded w-full" />
                    <textarea name="body" value={formData.body} onChange={handleChange} placeholder="Notes / paste email or text content here" rows="4" className="p-2 border rounded w-full"></textarea>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-3">
                        <button type="button" onClick={() => setIsFormOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            )}

            {isLoading && <p className="text-slate-500">Loading activity...</p>}
            {!isLoading && error && !isFormOpen && <p className="text-red-500">{error}</p>}
            {!isLoading && (
                <ul className="divide-y divide-gray-200">
                    {activities.map(activity => (
                        <li key={activity.activity_id} className="py-3">
                            <div className="flex justify-between items-baseline">
                                <span className="font-semibold text-slate-800">
                                    {activity.activity_type}
                                    {activity.direction !== 'N/A' && <span className="text-slate-500 font-normal"> ({activity.direction})</span>}
                                    {activity.subject && <span className="text-slate-600 font-normal"> — {activity.subject}</span>}
                                </span>
                                <span className="text-sm text-slate-500">{new Date(activity.occurred_at).toLocaleString()}</span>
                            </div>
                            {activity.body && <p className="text-slate-700 mt-1 whitespace-pre-wrap">{activity.body}</p>}
                            {activity.agent_name && <p className="text-xs text-slate-400 mt-1">Logged for: {activity.agent_name}</p>}
                        </li>
                    ))}
                    {activities.length === 0 && <p className="text-slate-500 py-4">No activity logged yet.</p>}
                </ul>
            )}
        </div>
    );
};

export default ActivityTimeline;
